import type { AppointmentType } from '../../../contexts/PsychologistContext';
import { addMinutesToTime, timeToMinutes } from './utils';

export interface BookingPatient {
  id?: string;
  name?: string;
  email: string;
  phone?: string;
  process?: string;
  processType?: string;
  timezone?: string;
  nationality?: string;
  hasNextSessionDate?: boolean;
}

export interface BookingWorkSchedule {
  id: string | number;
  psychologistId: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
}

/**
 * Strips accents/diacritical marks, lowercases, and trims.
 */
export function normalizeText(s?: string | null): string {
  return (s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Filters appointment types compatible with the patient's processType.
 * Falls back to all types if processType is empty or matches nothing.
 */
export function getCompatibleAppointmentTypes(
  baseTypes: AppointmentType[],
  processType?: string | null,
): AppointmentType[] {
  const pt = normalizeText(processType);
  if (!pt) return baseTypes;
  const filtered = baseTypes.filter(type => {
    const byService = normalizeText(type.service as unknown as string) === pt;
    const byName = normalizeText(type.name) === pt || normalizeText(type.name).includes(pt);
    return byService || byName;
  });
  return filtered.length > 0 ? filtered : baseTypes;
}

/**
 * Resolves the patient for scheduling: prefers selectedPatient,
 * then looks up by email. Returns null if not found (never returns mock).
 */
export function resolvePatientForScheduling(
  selectedPatient: BookingPatient | undefined | null,
  effectivePatients: BookingPatient[],
  email: string,
): BookingPatient | null {
  const found = selectedPatient ?? effectivePatients.find(px => px.email === email);
  return found ?? null;
}

/**
 * Resolves the effective appointment type via fallback chain:
 * 1. selectedType (if already set)
 * 2. Match by normalized name vs processType
 * 3. First available type
 * 4. Mock type (only for mock patients)
 * 5. null (for real patients when nothing available)
 */
export function resolveEffectiveAppointmentType(
  selectedType: AppointmentType | null | undefined,
  baseTypes: AppointmentType[],
  processType: string | undefined | null,
  isMockPatient: boolean,
): AppointmentType | null {
  return (
    selectedType
    ?? baseTypes.find(t => normalizeText(t.name) === normalizeText(processType || ''))
    ?? baseTypes[0]
    ?? (isMockPatient
      ? { id: 'mock-type', name: processType || 'Individual', duration: 60 } as AppointmentType
      : null)
  );
}

/**
 * Validates that the selected appointment type matches the patient's processType.
 * Skips validation when processType is empty/null.
 */
export function validateAppointmentTypeMatch(
  appointmentType: AppointmentType,
  processType?: string | null,
): boolean {
  if (!processType) return true;
  const selService = normalizeText(String((appointmentType as unknown as { service?: string })?.service));
  const selName = normalizeText(appointmentType.name);
  const patientPT = normalizeText(String(processType));
  return !!(
    (selService && selService === patientPT)
    || (selName && selName === patientPT)
    || (selName && selName.includes(patientPT))
  );
}

/**
 * Merges API-returned template with optimistic items from previous state.
 * Preserves optimistic items whose IDs are not yet in the API response.
 */
export function mergeTemplateWithOptimistic(
  apiTemplate: BookingWorkSchedule[],
  prevTemplate: BookingWorkSchedule[],
): BookingWorkSchedule[] {
  const tplIds = new Set(apiTemplate.map(t => String(t.id)));
  const optimistic = prevTemplate.filter(t => !tplIds.has(String(t.id)));
  return optimistic.length > 0 ? [...apiTemplate, ...optimistic] : apiTemplate;
}

export interface MergeableSession {
  id: string;
  date: string;
  time: string;
  status: string;
  origISO?: string;
  process_id?: string;
  patient: { email: string };
}

/**
 * ¿Esta sesión local (la que se agrega en el momento al agendar o reprogramar, antes de
 * que el backend la devuelva) ya tiene su sesión real en la respuesta del servidor?
 *
 * Al agendar se pinta una copia local para que aparezca al instante; cuando el backend
 * devuelve la real, la copia tiene que irse. La clave exacta `fecha-hora-email` no
 * alcanzaba: con el agendamiento nativo la real puede volver con el mail en otra
 * capitalización, con otro mail del paciente, o con la hora en otra zona que la copia
 * (la copia arma su `origISO` con la zona elegida en la Agenda y su fecha/hora con la
 * del navegador), y quedaban las dos en la grilla. Se las considera la misma si son del
 * mismo paciente (mail sin importar mayúsculas, o mismo proceso) y caen en el mismo
 * instante o en la misma fecha-hora mostrada.
 */
export function hasServerCounterpart(
  local: MergeableSession,
  server: MergeableSession[],
): boolean {
  if (local.status === 'blocked') return false;
  const localEmail = (local.patient.email || '').trim().toLowerCase();
  const localInstant = local.origISO ? new Date(local.origISO).getTime() : NaN;
  return server.some(s => {
    if (s.status === 'blocked') return false;
    if (s.id && s.id === local.id) return true;
    const samePatient =
      (!!localEmail && (s.patient.email || '').trim().toLowerCase() === localEmail) ||
      (!!local.process_id && s.process_id === local.process_id);
    if (!samePatient) return false;
    const serverInstant = s.origISO ? new Date(s.origISO).getTime() : NaN;
    const sameInstant = !Number.isNaN(localInstant) && localInstant === serverInstant;
    return sameInstant || (s.date === local.date && s.time === local.time);
  });
}

// ─── Inicios flexibles (marcas de 5 minutos) ─────────────────────────────────
//
// La psicóloga elige el slot en la grilla como siempre (:00 o :30) y recién en el
// modal de confirmación corre el inicio: 13:00 → 13:05, 13:10, …  La grilla de
// disponibilidad de api-core SIGUE siendo de 30 minutos: una sesión corrida no
// crea celdas nuevas, ocupa por redondeo todas las de 30 que pisa (13:10 + 60'
// ocupa 13:00, 13:30 y 14:00). Correr el inicio, entonces, consume 90 minutos de
// grilla para una sesión de 60.
//
// Qué se valida acá y qué no:
//  - SOLAPE contra sesiones y bloqueos → se valida. Es lo que de verdad rompe.
//    Los bloqueos manuales y de vacaciones llegan como sesiones con status
//    'blocked', y api-core NO los frena (no son AVAILABLE, así que su validación
//    los deja pasar): para esos, este modal es la única guarda.
//  - COBERTURA por un rango de trabajo configurado → NO se exige, a propósito.
//    api-core permite agendar en una celda sin disponibilidad; exigirla acá
//    apagaría el corrimiento de fin de jornada (atiende hasta 18:00, agenda
//    17:00 y quiere 17:10: pisa la celda de 18:00, que no tiene fila) cuando el
//    backend lo habría aceptado.

/** Paso de los inicios flexibles. api-core rechaza cualquier inicio que no sea múltiplo de esto. */
export const START_MINUTE_STEP = 5;

/** Paso de la grilla materializada (`psychologist_free_schedule`). No cambia con los inicios flexibles. */
export const GRID_CELL_MINUTES = 30;

/** Un tramo del día, en minutos desde medianoche. */
export interface BusyRange {
  startMin: number;
  endMin: number;
}

export interface StartTimeOption {
  /** Minutos corridos respecto del inicio del slot elegido (0, 5, 10, …). */
  offsetMin: number;
  startTime: string;
  endTime: string;
  /**
   * `false` → se muestra deshabilitado. Se ofrece igual, en vez de esconderlo, para
   * que se vea por qué ese horario no está disponible.
   */
  enabled: boolean;
  /** Motivo cuando `enabled` es false. */
  disabledReason?: 'overlap';
}

/**
 * Celdas de 30' que una sesión ocupa por redondeo, ancladas a :00/:30.
 * Espeja el helper de grilla de api-core: 13:10 + 60' → [13:00, 13:30, 14:00].
 */
export function gridCellsForSession(startMin: number, durationMin: number): number[] {
  if (durationMin <= 0) return [];
  const firstCell = Math.floor(startMin / GRID_CELL_MINUTES) * GRID_CELL_MINUTES;
  const endMin = startMin + durationMin;
  const cells: number[] = [];
  for (let cell = firstCell; cell < endMin; cell += GRID_CELL_MINUTES) {
    cells.push(cell);
  }
  return cells;
}

/** ¿Se pisan dos tramos? Tocarse en el borde (uno termina donde arranca el otro) no es pisarse. */
export function rangesOverlap(a: BusyRange, b: BusyRange): boolean {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

export interface BuildStartTimeOptionsParams {
  /** Inicio del slot elegido en la grilla ("13:00"). */
  slotStartTime: string;
  /** Duración del tipo de cita, en minutos. */
  durationMin: number;
  /** Sesiones y bloqueos del día, sin incluir la sesión que se está reprogramando. */
  busy?: BusyRange[];
  /**
   * Hasta dónde se puede correr. Por defecto hasta justo antes de la próxima celda
   * de 30: el slot de 13:00 ofrece hasta 13:25, porque 13:30 ya es un slot propio.
   */
  maxOffsetMin?: number;
}

/**
 * Inicios posibles para un slot ya elegido, en pasos de 5 minutos.
 * El primero (offset 0) es el horario de siempre: sin corrimiento, el payload que
 * sale es idéntico al de hoy.
 */
export function buildStartTimeOptions({
  slotStartTime,
  durationMin,
  busy = [],
  maxOffsetMin = GRID_CELL_MINUTES - START_MINUTE_STEP,
}: BuildStartTimeOptionsParams): StartTimeOption[] {
  const slotStartMin = timeToMinutes(slotStartTime);
  const options: StartTimeOption[] = [];

  for (let offsetMin = 0; offsetMin <= maxOffsetMin; offsetMin += START_MINUTE_STEP) {
    const startMin = slotStartMin + offsetMin;
    // Nunca ofrecer un inicio que cruce la medianoche: el corrimiento mueve la hora pero no
    // la fecha, así que 23:50 + 15 viajaría como 00:05 del MISMO día, un día antes. Con la
    // grilla actual no se llega (el último slot es 23:30 → 23:55), pero si alguien sube
    // maxOffsetMin, que falte la opción es mejor que agendar en el día equivocado.
    if (startMin >= 24 * 60) break;
    const candidate: BusyRange = { startMin, endMin: startMin + durationMin };
    const overlaps = busy.some(b => rangesOverlap(candidate, b));

    options.push({
      offsetMin,
      startTime: addMinutesToTime(slotStartTime, offsetMin),
      endTime: addMinutesToTime(slotStartTime, offsetMin + durationMin),
      enabled: !overlaps,
      ...(overlaps ? { disabledReason: 'overlap' as const } : {}),
    });
  }

  return options;
}

/** Un tramo consumido por el redondeo de la grilla que la sesión no usa. */
export interface DeadGridSegment {
  /** 'pre' = antes del inicio de la sesión; 'post' = después de que termina. */
  key: 'pre' | 'post';
  fromMin: number;
  toMin: number;
}

/**
 * Tramos que la grilla ocupa pero la sesión no usa.
 *
 * Una sesión 18:05–19:05 deja tomadas las celdas de 18:00 y de 19:00 enteras, así que
 * 18:00–18:05 y 19:05–19:30 quedan bloqueados y vacíos: media hora de agenda que se
 * pierde sin que se vea en ningún lado. Con un inicio alineado no hay tramos muertos.
 *
 * @param rowStartMin recorta al tramo visible de la fila que está dibujando la celda.
 */
export function gridDeadSegments(
  startMin: number,
  durationMin: number,
  rowStartMin = 0,
): DeadGridSegment[] {
  if (durationMin <= 0) return [];
  const endMin = startMin + durationMin;
  const cellStart = Math.floor(startMin / GRID_CELL_MINUTES) * GRID_CELL_MINUTES;
  const cellEnd = Math.ceil(endMin / GRID_CELL_MINUTES) * GRID_CELL_MINUTES;
  const segments: DeadGridSegment[] = [];
  if (startMin > cellStart) segments.push({ key: 'pre', fromMin: cellStart, toMin: startMin });
  if (cellEnd > endMin) segments.push({ key: 'post', fromMin: endMin, toMin: cellEnd });
  return segments
    .map(seg => ({ ...seg, fromMin: Math.max(seg.fromMin, rowStartMin) }))
    .filter(seg => seg.toMin > seg.fromMin);
}
